import { useState } from "react";
import { useT, translateError } from "../i18n/hooks.ts";
import type { SubBasinLayer } from "../model/gisLayer.ts";
import {
  updateParameterValue,
  updateStateVariableValue,
  addZone,
  assignZoneInBulk,
  changeZoneModel,
  deleteZone,
  hasUnreplicatedOutletPoints,
  renameZone,
  replicateOutletPointsAsZones,
} from "../model/projectEntities.ts";
import type { EvalHidProject } from "../model/types.ts";
import { Dialog } from "./Dialog.tsx";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.tsx";
import { InfoIcon } from "./InfoIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { BulkAssigner } from "./wizard/BulkAssigner.tsx";
import { AssignmentMap } from "./wizard/AssignmentMap.tsx";

/**
 * Create zones (each with a rainfall-runoff or snow model, and its default
 * parameters) and assign them to sub-basins, in bulk (like Zonas.vb +
 * AsignacionZonas.vb) or by clicking on the map (like AsigZona.vb + GIS.vb,
 * if the project has a shapefile). Assigning the zone also fixes the
 * sub-basin's model — there's no separate step for that.
 *
 * Lives directly under ui/ (not wizard/), even though it started as the
 * new-project wizard's own Step 2 (NewProject.tsx still uses it there) —
 * EditBar.tsx's "Zonas" editor panel reuses the exact same component, and
 * this way neither the wizard nor the editor tabs have to import it from
 * the other. Same reasoning as TopologyAssignmentSection.tsx, which single-
 * sourced this same wizard/editor split for outlet point/aquifer
 * assignment a while ago — see project_evalhid_code_audit.md.
 */
export function Step2Zones({
  project,
  gisLayer,
  onChange,
  selection: mapSelection,
  onChangeSelection: onChangeMapSelection,
  assignMode: controlledAssignMode,
  onChangeAssignMode,
}: {
  project: EvalHidProject;
  gisLayer: SubBasinLayer | null;
  onChange: (project: EvalHidProject) => void;
  /**
   * Selection shared with the main map (editor context — see EditBar.tsx):
   * when given, clicking sub-basins there selects them here too, and the
   * small selection map below (only useful when there's no main map to
   * click on, e.g. the new-project wizard) isn't shown.
   */
  selection?: Set<number>;
  onChangeSelection?: (selection: Set<number>) => void;
  /**
   * Which assignment table (zone vs. snow zone) is showing — controlled,
   * same reasoning as `selection`: in the editor (EditBar.tsx) App.tsx
   * needs to know this too, to color sub-basins on the map/cards by the
   * matching field (see assignmentColors.ts). Uncontrolled (the wizard,
   * which has no such coloring) falls back to local state.
   */
  assignMode?: "zone" | "snow";
  onChangeAssignMode?: (mode: "zone" | "snow") => void;
}) {
  const [name, setName] = useState("");
  const [modelTypeCode, setModelTypeCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [replicateModelCode, setReplicateModelCode] = useState("");
  const [replicateError, setReplicateError] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [localRunoffSelection, setLocalRunoffSelection] = useState<Set<number>>(new Set());
  const [localSnowSelection, setLocalSnowSelection] = useState<Set<number>>(new Set());
  const [deletingZone, setDeletingZone] = useState<number | null>(null);
  const [localAssignMode, setLocalAssignMode] = useState<"zone" | "snow">("zone");
  const assignMode = controlledAssignMode ?? localAssignMode;
  const setAssignMode = onChangeAssignMode ?? setLocalAssignMode;
  const t = useT();

  const usingMainMap = mapSelection != null && onChangeMapSelection != null;
  const runoffSelection = usingMainMap ? mapSelection : localRunoffSelection;
  const setRunoffSelection = usingMainMap ? onChangeMapSelection : setLocalRunoffSelection;
  const snowSelection = usingMainMap ? mapSelection : localSnowSelection;
  const setSnowSelection = usingMainMap ? onChangeMapSelection : setLocalSnowSelection;

  const runoffZones = project.zones.filter((z) => z.modelTypeCode != null);
  const snowZones = project.zones.filter((z) => z.snowModelTypeCode != null);
  const runoffModels = project.modelTypes.filter((m) => m.kind === 0);
  const canReplicateOutlets = hasUnreplicatedOutletPoints(project);
  // Only worth a switch when there's actually a choice — a project with just
  // one zone kind defined skips straight to it, no tabs to pick from.
  const showZoneModeSwitch = runoffZones.length > 0 && snowZones.length > 0;
  const effectiveAssignMode: "zone" | "snow" =
    runoffZones.length === 0 ? "snow" : snowZones.length === 0 ? "zone" : assignMode;

  function create() {
    setError(null);
    if (!modelTypeCode) {
      setError(t("step2.chooseModel"));
      return;
    }
    const [code, kind] = modelTypeCode.split("-").map(Number);
    try {
      onChange(addZone(project, name, code, kind));
      setName("");
      setModelTypeCode("");
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  const zone = project.zones.find((z) => z.zoneCode === editingZone) ?? null;
  const zoneToDelete = project.zones.find((z) => z.zoneCode === deletingZone) ?? null;
  const zoneParameters = zone
    ? project.parameterValues
        .filter((v) => v.zoneCode === zone.zoneCode)
        .map((v) => ({
          v,
          def: project.parameterDefinitions.find(
            (d) => d.parameterCode === v.parameterCode && d.kind === v.kind
          ),
        }))
    : [];
  const zoneStateVariables = zone
    ? project.stateVariableValues
        .filter((v) => v.zoneCode === zone.zoneCode)
        .map((v) => ({
          v,
          def: project.stateVariableDefinitions.find(
            (d) => d.stateVariableCode === v.stateVariableCode && d.kind === v.kind
          ),
        }))
    : [];
  // A zone never changes kind (rainfall-runoff/snow) — only its model within
  // that kind, see changeZoneModel in projectEntities.ts.
  const zoneModelKind = zone?.modelTypeCode != null ? 0 : 1;
  const currentZoneModel = zone
    ? zoneModelKind === 0
      ? zone.modelTypeCode
      : zone.snowModelTypeCode
    : null;
  const modelsOfSameKind = project.modelTypes.filter((m) => m.kind === zoneModelKind);

  return (
    <div>
      <h3>
        2. {t("panels.zones")} <InfoIcon text={t("step2.zoneHelp")} />
      </h3>

      {usingMainMap && project.outletPoints.length > 0 && (
        <div className="step-form step-form--tight">
          <select
            value={replicateModelCode}
            onChange={(e) => setReplicateModelCode(e.target.value)}
          >
            <option value="">{t("common.modelPlaceholder")}</option>
            {runoffModels.map((m) => (
              <option key={m.modelTypeCode} value={m.modelTypeCode}>
                {m.modelTypeName}
              </option>
            ))}
          </select>
          <button
            className="button"
            disabled={!canReplicateOutlets}
            title={canReplicateOutlets ? undefined : t("step2.replicateOutletsUpToDate")}
            onClick={() => {
              if (!replicateModelCode) {
                setReplicateError(t("step2.replicateOutletsChooseModel"));
                return;
              }
              setReplicateError(null);
              onChange(replicateOutletPointsAsZones(project, Number(replicateModelCode)));
            }}
          >
            {t("step2.replicateOutletsButton")}
          </button>
          <InfoIcon text={t("step2.replicateOutletsHelp")} />
          {replicateError && <p className="error">{replicateError}</p>}
        </div>
      )}

      {usingMainMap && mapSelection.size > 0 && (
        <div className="edit-bar-selection">
          <div className="edit-bar-selection-header">
            <strong>
              {t(
                mapSelection.size > 1
                  ? "subBasinsPanel.multipleSelected"
                  : "subBasinsPanel.oneSelected",
                { n: mapSelection.size }
              )}
            </strong>
            <div className="edit-bar-selection-actions">
              <button
                className="button-icon"
                title={t("subBasinsPanel.clearSelection")}
                onClick={() => onChangeMapSelection(new Set())}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="step-form">
        <input
          placeholder={t("step2.zoneNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select value={modelTypeCode} onChange={(e) => setModelTypeCode(e.target.value)}>
          <option value="">{t("common.modelPlaceholder")}</option>
          {project.modelTypes.map((m) => (
            <option key={`${m.kind}-${m.modelTypeCode}`} value={`${m.modelTypeCode}-${m.kind}`}>
              {m.modelTypeName} {m.kind === 1 ? t("step2.snowSuffix") : ""}
            </option>
          ))}
        </select>
        <button className="button" onClick={create}>
          {t("step2.createZone")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      {project.zones.length > 0 && (
        <div className="table-scroll catalog-table-scroll catalog-table-scroll-capped">
          <table>
            <thead>
              <tr>
                <th>{t("common.name")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {project.zones.map((z) => (
                <tr
                  key={z.zoneCode}
                  onClick={() => {
                    setEditingZone(z.zoneCode);
                    setEditName(z.name);
                    setEditError(null);
                  }}
                >
                  <td>{z.name}</td>
                  <td>
                    <button
                      className="button-icon button-danger"
                      title={t("step2.deleteZonePermanent")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingZone(z.zoneCode);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {zoneToDelete && (
        <ConfirmDeleteDialog
          title={t("step2.deleteZoneDialogTitle")}
          name={zoneToDelete.name}
          onConfirm={() => onChange(deleteZone(project, zoneToDelete.zoneCode))}
          onClose={() => setDeletingZone(null)}
        />
      )}

      {zone && (
        <Dialog title={t("step2.editZoneTitle")} onClose={() => setEditingZone(null)}>
          <div className="step-form">
            <input value={editName} onChange={(e) => setEditName(e.target.value)} />
            {editError && <p className="error">{editError}</p>}
            <button
              className="button button-primary"
              onClick={() => {
                try {
                  onChange(renameZone(project, zone.zoneCode, editName));
                  setEditError(null);
                } catch (err) {
                  setEditError(translateError(err, t));
                }
              }}
            >
              {t("common.rename")}
            </button>
          </div>

          <div className="step-form">
            <label>
              {t("common.model")}
              <InfoIcon text={t("step2.changeModelWarning")} />
              <select
                value={currentZoneModel ?? ""}
                onChange={(e) =>
                  onChange(changeZoneModel(project, zone.zoneCode, Number(e.target.value)))
                }
              >
                {modelsOfSameKind.map((m) => (
                  <option key={m.modelTypeCode} value={m.modelTypeCode}>
                    {m.modelTypeName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <h4>
            {t("step2.parametersOf", { name: zone.name })}{" "}
            <InfoIcon text={t("step2.parameterScaleWarning")} />
          </h4>
          <table>
            <thead>
              <tr>
                <th>{t("common.parameter")}</th>
                <th>{t("common.value")}</th>
                <th>{t("common.units")}</th>
              </tr>
            </thead>
            <tbody>
              {zoneParameters.map(({ v, def }) => (
                <tr key={v.parameterCode}>
                  <td title={def?.longName ?? ""}>{def?.name ?? v.parameterCode}</td>
                  <td>
                    <input
                      type="number"
                      value={v.value ?? 0}
                      onChange={(e) =>
                        onChange(
                          updateParameterValue(
                            project,
                            zone.zoneCode,
                            v.parameterCode,
                            v.kind,
                            Number(e.target.value)
                          )
                        )
                      }
                    />
                  </td>
                  <td>{def?.units}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {zoneStateVariables.length > 0 && (
            <>
              <h4>{t("step2.stateVariablesOf", { name: zone.name })}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t("common.variable")}</th>
                    <th>{t("common.initialValue")}</th>
                    <th>{t("common.units")}</th>
                  </tr>
                </thead>
                <tbody>
                  {zoneStateVariables.map(({ v, def }) => (
                    <tr key={v.stateVariableCode}>
                      <td title={def?.longName ?? ""}>{def?.name ?? v.stateVariableCode}</td>
                      <td>
                        <input
                          type="number"
                          value={v.value ?? 0}
                          onChange={(e) =>
                            onChange(
                              updateStateVariableValue(
                                project,
                                zone.zoneCode,
                                v.stateVariableCode,
                                v.kind,
                                Number(e.target.value)
                              )
                            )
                          }
                        />
                      </td>
                      <td>{def?.units}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Dialog>
      )}

      {(runoffZones.length > 0 || snowZones.length > 0) && (
        <>
          <h4>
            {t(
              effectiveAssignMode === "zone" ? "step2.assignZoneTitle" : "step2.assignSnowZoneTitle"
            )}{" "}
            <InfoIcon
              text={t(
                // Same reasoning as TopologyAssignmentSection.tsx's own
                // identical branch: with the main map/canvas (EditBar.tsx),
                // the actual selection surface is whichever view is active
                // there — this component's own little AssignmentMap.tsx
                // below is only rendered (and only accurate to describe)
                // when !usingMainMap.
                usingMainMap ? "step2.mapOrCardsAssignHelp" : "step2.mapAssignHelp"
              )}
            />
          </h4>

          {showZoneModeSwitch && (
            <div className="edit-panel-tabs">
              <button
                className={effectiveAssignMode === "zone" ? "tab-active" : undefined}
                onClick={() => setAssignMode("zone")}
              >
                {t("resultsPanel.zone")}
              </button>
              <button
                className={effectiveAssignMode === "snow" ? "tab-active" : undefined}
                onClick={() => setAssignMode("snow")}
              >
                {t("subBasinsPanel.snowZone")}
              </button>
            </div>
          )}

          {effectiveAssignMode === "zone" ? (
            <>
              {gisLayer && !usingMainMap && (
                <AssignmentMap
                  gisLayer={gisLayer}
                  project={project}
                  isAssigned={(c) => c.zoneCode != null}
                  selection={runoffSelection}
                  onChangeSelection={setRunoffSelection}
                />
              )}
              <BulkAssigner
                subBasins={project.subBasins}
                valueLabel={(c) =>
                  c.zoneCode == null
                    ? t("common.unassigned")
                    : (runoffZones.find((z) => z.zoneCode === c.zoneCode)?.name ?? `#${c.zoneCode}`)
                }
                options={runoffZones.map((z) => ({ value: z.zoneCode, label: z.name }))}
                buttonText={t("step2.assignZoneButton")}
                selection={runoffSelection}
                onChangeSelection={setRunoffSelection}
                unassignValue={null}
                onAssign={(codes, value) =>
                  onChange(assignZoneInBulk(project, codes, "zoneCode", value))
                }
              />
            </>
          ) : (
            <>
              {gisLayer && !usingMainMap && (
                <AssignmentMap
                  gisLayer={gisLayer}
                  project={project}
                  isAssigned={(c) => c.snowZoneCode != null}
                  selection={snowSelection}
                  onChangeSelection={setSnowSelection}
                />
              )}
              <BulkAssigner
                subBasins={project.subBasins}
                valueLabel={(c) =>
                  c.snowZoneCode == null
                    ? t("common.unassigned")
                    : (snowZones.find((z) => z.zoneCode === c.snowZoneCode)?.name ??
                      `#${c.snowZoneCode}`)
                }
                options={snowZones.map((z) => ({ value: z.zoneCode, label: z.name }))}
                buttonText={t("step2.assignSnowZoneButton")}
                selection={snowSelection}
                onChangeSelection={setSnowSelection}
                unassignValue={null}
                onAssign={(codes, value) =>
                  onChange(assignZoneInBulk(project, codes, "snowZoneCode", value))
                }
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
